import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { Box, Stack, Avatar, Typography, IconButton } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import PeopleAltRounded from "@mui/icons-material/PeopleAltRounded";
import AccessTimeRounded from "@mui/icons-material/AccessTimeRounded";
import WorkHistory from "@mui/icons-material/WorkHistory";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Cancel from "@mui/icons-material/Cancel";
import AssignmentLate from "@mui/icons-material/AssignmentLate";
import ExitToApp from "@mui/icons-material/ExitToApp";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import { useSelector } from "react-redux";
import moment from "moment";
import { useFetch } from "services/hooks/useFetch";
import { Loader } from "components/Loader";
import { theme, defaultAllSelection, optionalAvathar } from "services/constants";
import { showToast } from "services/utils/Status";
import sessionHandling from "services/utils/notificationUtils";
import { checkViewerPermission, subModuleNameMatches } from "services/utils/HelperFunction";
import { FilterProvider } from "pages/FilterContext";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { getThisMonthRange } from "components/CustomComponents/dateRangeDefaults";
import { CompactHeaderSelectFilter } from "components/CustomComponents/CompactHeaderSelectFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import {
  useExcelDownload,
  MAX_PDF_ROWS,
  MAX_PDF_COLUMNS,
} from "components/CustomComponents/useExcelDownload";
import {
  designColors,
  designShell,
  paginationFooter,
  rowsPerPageOptions,
  StatCard,
  withDesignFont,
} from "services/constants/designTokens";
import { approveStickyFooterBar } from "pages/TeamZone/Approve/approveUi";
import {
  EmployeeAttendanceSummaryHeader,
  EmployeeAttendanceDetailHeader,
} from "pages/TeamZone/TeamZoneHeaders";

// Identity fields are the card's fixed heading (avatar + name + id +
// department) rather than a togglable column — kept out of the saved-view
// header set, but always included in the grouped export regardless of which
// stat fields a saved view shows/hides.
const IDENTITY_EXPORT_HEADERS = [
  { name: "Employee Name", key: "Name" },
  { name: "Employee Id", key: "employee_id" },
  { name: "Department", key: "Dept" },
];

const SEARCH_FILTER_HEADERS = [
  { name: "Employee Name", key: "Name", type: "string" },
  { name: "Employee Id", key: "employee_id", type: "string" },
  { name: "Department", key: "Dept", type: "autocomplete" },
];

const defaultEmployeeOption = {
  id: defaultAllSelection,
  label: "All",
  user_id: defaultAllSelection,
  user_name: "All",
  employee_id: "All",
};

// "704:30" -> 704*60+30 minutes. Returns null for markers like "-"/"WO" so
// callers can tell "no duration" apart from a genuine zero duration.
const parseDurationToMinutes = (value) => {
  const match = /^(-?)(\d+):(\d{2})$/.exec(`${value ?? ""}`.trim());
  if (!match) return null;
  const sign = match[1] === "-" ? -1 : 1;
  return sign * (Number(match[2]) * 60 + Number(match[3]));
};

const formatMinutesAsDuration = (totalMinutes) => {
  if (!Number.isFinite(totalMinutes)) return "00:00";
  const sign = totalMinutes < 0 ? "-" : "";
  const absMinutes = Math.round(Math.abs(totalMinutes));
  const hours = Math.floor(absMinutes / 60);
  const minutes = absMinutes % 60;
  return `${sign}${hours.toLocaleString()}:${String(minutes).padStart(2, "0")}`;
};

const parseRecordDate = (dateStr) => {
  const [day, month, year] = `${dateStr || ""}`.split("-");
  return new Date(`${year}-${month}-${day}`);
};

const withTemporaryCheckSuccess = (payload) => {
  if (!payload || !Array.isArray(payload.data)) return payload || {};

  return {
    ...payload,
    data: payload.data.map((item) => ({
      ...item,
      summary: {
        ...(item?.summary || {}),
        check_success: true,
      },
      daily_records: Array.isArray(item?.daily_records)
        ? item.daily_records.map((record) => ({
            ...record,
            check_success: true,
          }))
        : [],
    })),
  };
};

export const EsslReport = ({
  setShowReport,
  setReportHeaderActions,
  embeddedReportTypes = null,
  selectedReportType = null,
  onReportTypeChange = null,
}) => {
  const {
    domain,
    token,
    userDetails,
    topbarCompanyId,
    topbarCompanyName,
    userScreenAccess,
  } = useSelector((state) => state.tokenReducer);

  const [departId, setDepartId] = useState(defaultAllSelection);
  const [departmentName, setDepartmentName] = useState("All Department");
  const [userId, setUserId] = useState(defaultAllSelection);
  const [selectedEmployee, setSelectedEmployee] = useState(defaultEmployeeOption);
  const [initialDateRange] = useState(() =>
    getThisMonthRange({ endAt: "today" }),
  );
  const [startDate, setStartDate] = useState(initialDateRange.startDate);
  const [endDate, setEndDate] = useState(initialDateRange.endDate);
  const [dateRangeLabel, setDateRangeLabel] = useState(initialDateRange.label);
  const [isLoading, setIsLoading] = useState(false);
  const [esslReportData, setEsslReportData] = useState(null);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [collapsedEmployees, setCollapsedEmployees] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Measures the sticky footer bar's own height so the page can reserve
  // that much bottom padding — otherwise the last card's rows sit hidden
  // behind the footer once the page scroll carries it into its sticky
  // position (same pattern as Approve/Leave's containerRef/footerRef).
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const footerRef = useCallback((node) => setFooterNode(node), []);

  useEffect(() => {
    if (!footerNode) return undefined;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  const {
    downloadLegacyAttendanceExcel,
    downloadLegacyAttendancePdf,
    downloadLegacyAttendanceCsv,
  } = useExcelDownload();

  const { result: allDepartmentDetails } = useFetch(
    `${domain}get-department/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
  );
  const { result: membersData, setLoad: reCallMemberApi } = useFetch(
    `${domain}get-department-based-user/?department_id=${departId}&user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
  );

  const departmentList = useMemo(
    () => [
      { id: defaultAllSelection, label: "All Department", department_name: "All Department" },
      ...(allDepartmentDetails ?? []).map((obj) => ({ ...obj, label: obj.department_name })),
    ],
    [allDepartmentDetails],
  );

  const employeeOptions = useMemo(
    () => [
      defaultEmployeeOption,
      ...(membersData
        ?.filter((obj) => obj?.user_name !== "All")
        ?.map((obj) => ({ ...obj, label: obj?.user_name })) ?? []),
    ],
    [membersData],
  );

  const employeeIdOptions = useMemo(
    () => employeeOptions.map((obj) => ({ ...obj, label: obj?.employee_id || "N/A" })),
    [employeeOptions],
  );

  const teamZoneReports = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Reports",
  );
  // Real payloads return "Employee Attendance" (no "Report" suffix) for
  // this screen's sub_module_name — subModuleNameMatches applies the same
  // alias table used elsewhere instead of a raw === that would silently
  // land on undefined and send an invalid submodule id when saving a view.
  const subModuleId = teamZoneReports?.submodules?.find((sub) =>
    subModuleNameMatches(sub.sub_module_name, "Employee Attendance Report"),
  )?.sub_module_id;

  // Viewer permission on this submodule is enough to download the report
  // and customize/save its column view -- matching every other Reports
  // screen (WorkingHours, DailyReport, Anomalies, etc.), which all gate
  // both the CustomFilter download/export controls and useSavedViewSelector
  // off a single checkViewerPermission call rather than requiring editor
  // rights. Editor permission isn't meaningful here since reports have no
  // editable data.
  const check_access = checkViewerPermission(
    userScreenAccess?.module_details,
    "Reports",
    "Employee Attendance Report",
  );

  const checkAccess = check_access;
  const canSaveViews = check_access;

  const summaryHeaderKeys = useMemo(
    () => new Set(EmployeeAttendanceSummaryHeader.map((header) => header.key)),
    [],
  );
  const combinedFullHeaders = useMemo(
    () => [
      ...EmployeeAttendanceSummaryHeader.map((header) => ({
        ...header,
        groupName: "Employee Summary",
      })),
      ...EmployeeAttendanceDetailHeader.map((header) => ({
        ...header,
        groupName: "Attendance Details",
      })),
    ],
    [],
  );

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const { visibleHeaders: combinedVisibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "employee_attendance_report_saved_views",
    fullHeaders: combinedFullHeaders,
    screenAccess: canSaveViews,
    onViewChange: handleSavedViewChange,
  });
  const visibleSummaryHeaders = useMemo(
    () => combinedVisibleHeaders.filter((header) => summaryHeaderKeys.has(header.key)),
    [combinedVisibleHeaders, summaryHeaderKeys],
  );
  const visibleDetailHeaders = useMemo(
    () => combinedVisibleHeaders.filter((header) => !summaryHeaderKeys.has(header.key)),
    [combinedVisibleHeaders, summaryHeaderKeys],
  );

  useEffect(() => {
    if (!topbarCompanyId) return undefined;

    let ignoreStaleResponse = false;

    const fetchEmployeeAttendance = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(
          `${domain}essl-report/?user_id=${userDetails?.id}&start_date=${startDate}&end_date=${endDate}&department_id=${departId}&designation_id=${defaultAllSelection}&company_id=${topbarCompanyId}&selected_user_id=${userId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (ignoreStaleResponse) return;

        if (response.ok) {
          setEsslReportData(withTemporaryCheckSuccess(res));
        } else if (response.status === 409) {
          setEsslReportData({});
          sessionHandling();
        } else {
          setEsslReportData({});
          throw new Error(res?.error || "Failed to load employee attendance report");
        }
      } catch (error) {
        if (ignoreStaleResponse) return;
        setEsslReportData({});
        showToast(
          "error",
          error.message || "Couldn't load the employee attendance report. Please try again.",
        );
      } finally {
        if (!ignoreStaleResponse) setIsLoading(false);
      }
    };

    fetchEmployeeAttendance();

    return () => {
      ignoreStaleResponse = true;
    };
  }, [domain, token, userDetails?.id, startDate, endDate, departId, topbarCompanyId, userId]);

  // Two defenses against a card rendering stale/mismatched data after the
  // date range is switched a few times:
  //  - __key must be unique per row. This API doesn't always return
  //    employee_id, and two employees can share the same Name — falling back
  //    to Name alone then produces duplicate React keys, which makes React
  //    reuse a stale fiber/DOM subtree for one of the rows on the next
  //    render (observed live with two "Testing" accounts: after switching
  //    ranges, one of their cards kept showing the previous range's data).
  //    Appending the array index guarantees uniqueness.
  //  - __dailyRecords is clipped to [startDate, endDate] so the matrix table
  //    never renders more day-columns than the currently selected range,
  //    regardless of what the response contains.
  const summaryRows = useMemo(() => {
    const rangeStart = moment(startDate, "YYYY-MM-DD");
    const rangeEnd = moment(endDate, "YYYY-MM-DD");
    return (esslReportData?.data || []).map((item, index) => ({
      ...item.summary,
      __key: `${item?.summary?.employee_id ?? item?.summary?.Name ?? "row"}-${index}`,
      __dailyRecords: (item.daily_records || []).filter((day) => {
        const recordDate = moment(day?.Date, "DD-MM-YYYY");
        return recordDate.isValid() && recordDate.isBetween(rangeStart, rangeEnd, "day", "[]");
      }),
    }));
  }, [esslReportData, startDate, endDate]);

  // Sync filteredEmployees during render (not via useEffect) so it never
  // lags a render behind summaryRows. An effect-based sync commits/paints
  // one frame with the new (non-empty) summaryRows but the still-stale
  // (empty) filteredEmployees, which briefly renders "No matching data
  // found for your search" before the effect catches up. Adjusting state
  // while rendering applies before the browser paints, so that flash never
  // shows. See https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [prevSummaryRows, setPrevSummaryRows] = useState(summaryRows);
  if (summaryRows !== prevSummaryRows) {
    setPrevSummaryRows(summaryRows);
    setFilteredEmployees(summaryRows);
    setPage(0);
  }

  const handleFilteredDataChange = useCallback((nextRows) => {
    setFilteredEmployees(nextRows);
    setPage(0);
  }, []);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // "Late Days"/"Early Going Days" and the % cards below read as day counts
  // across the whole filtered roster, not per-employee durations, so they're
  // derived here rather than pulled from a single summary field. The % denominator
  // is every employee's day-in-range slot (headcount x days in the selected
  // range) — the same basis Present/Absent/Late/Early all share, so the four
  // percentages stay comparable to one another.
  const attendanceStats = useMemo(() => {
    const totalEmployees = summaryRows.length;
    let totalWorkingMinutes = 0;
    let presentDaysTotal = 0;
    let absentDaysTotal = 0;
    let earlyGoingDaysTotal = 0;
    let lateDaysTotal = 0;

    summaryRows.forEach((summary) => {
      totalWorkingMinutes += parseDurationToMinutes(summary.Total_Working_Hrs) || 0;
      presentDaysTotal += Number(summary.Present_Days) || 0;
      absentDaysTotal += Number(summary.Absent_Days) || 0;
      earlyGoingDaysTotal += Number(summary.Early_Going_By_Days) || 0;
      lateDaysTotal += (summary.__dailyRecords || []).filter(
        (day) => (parseDurationToMinutes(day?.Late_By) || 0) > 0,
      ).length;
    });

    const rangeDays = Math.max(
      1,
      moment(endDate).diff(moment(startDate), "days") + 1,
    );
    const totalPossibleDays = totalEmployees * rangeDays;
    const pctOfDays = (value) =>
      totalPossibleDays > 0 ? `${((value / totalPossibleDays) * 100).toFixed(1)}% of total days` : "-";

    return {
      totalEmployees,
      avgWorkingHrsPerDay: formatMinutesAsDuration(
        presentDaysTotal > 0 ? totalWorkingMinutes / presentDaysTotal : 0,
      ),
      totalWorkingHrs: formatMinutesAsDuration(totalWorkingMinutes),
      presentDaysTotal,
      absentDaysTotal,
      lateDaysTotal,
      earlyGoingDaysTotal,
      presentDetail: pctOfDays(presentDaysTotal),
      absentDetail: pctOfDays(absentDaysTotal),
      lateDetail: pctOfDays(lateDaysTotal),
      earlyDetail: pctOfDays(earlyGoingDaysTotal),
    };
  }, [summaryRows, startDate, endDate]);

  const summaryStatCards = useMemo(
    () => [
      {
        key: "total-employees",
        title: "Total Employees",
        value: attendanceStats.totalEmployees,
        detail: "In selected range",
        icon: PeopleAltRounded,
        accent: designColors.tealDark,
        tint: designColors.tealTint,
      },
      {
        key: "avg-working-hrs",
        title: "Avg Working Hrs / Day",
        value: attendanceStats.avgWorkingHrsPerDay,
        detail: "Per present day",
        icon: AccessTimeRounded,
        accent: "#2563EB",
        tint: "#EEF4FF",
      },
      {
        key: "total-working-hrs",
        title: "Total Working Hrs",
        value: attendanceStats.totalWorkingHrs,
        detail: "In selected range",
        icon: WorkHistory,
        accent: "#7C3AED",
        tint: "#F5F3FF",
      },
      {
        key: "present-days",
        title: "Present Days",
        value: attendanceStats.presentDaysTotal,
        detail: attendanceStats.presentDetail,
        icon: CheckCircle,
        accent: "#12905B",
        tint: "#EEF9F3",
      },
      {
        key: "absent-days",
        title: "Absent Days",
        value: attendanceStats.absentDaysTotal,
        detail: attendanceStats.absentDetail,
        icon: Cancel,
        accent: "#DC2626",
        tint: "#FEF2F2",
      },
      {
        key: "late-days",
        title: "Late Days",
        value: attendanceStats.lateDaysTotal,
        detail: attendanceStats.lateDetail,
        icon: AssignmentLate,
        accent: "#D97706",
        tint: "#FFF6EA",
      },
      {
        key: "early-going-days",
        title: "Early Going Days",
        value: attendanceStats.earlyGoingDaysTotal,
        detail: attendanceStats.earlyDetail,
        icon: ExitToApp,
        accent: "#0EA5E9",
        tint: "#EFF8FF",
      },
    ],
    [attendanceStats],
  );

  const handleDepartmentName = (event, data) => {
    setDepartId(data?.id ?? defaultAllSelection);
    setDepartmentName(data?.label || "All Department");
    setSelectedEmployee(defaultEmployeeOption);
    setUserId(defaultAllSelection);
    reCallMemberApi(true);
    setPage(0);
  };

  const updateEmployee = (event, data) => {
    setSelectedEmployee(data || defaultEmployeeOption);
    setUserId(data?.user_id ?? defaultAllSelection);
    setPage(0);
  };

  const handleStartDate = (rangeStart, rangeEnd, rangeLabel) => {
    const parsedStartDate = moment(rangeStart);
    const parsedEndDate = moment(rangeEnd);

    if (!parsedStartDate.isValid() || !parsedEndDate.isValid()) {
      return;
    }

    setStartDate(parsedStartDate.format("YYYY-MM-DD"));
    setEndDate(parsedEndDate.format("YYYY-MM-DD"));
    setDateRangeLabel(rangeLabel || "Custom Range");
    setPage(0);
  };

  const getReportMetadata = useCallback(
    () => ({
      "Company Name": topbarCompanyName || "All",
      Department: departmentName || "All Department",
      Employee: selectedEmployee?.user_name || "All",
      "Date Range": `${moment(startDate).format("DD/MM/YYYY")} to ${moment(endDate).format("DD/MM/YYYY")}`,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
      "Total Employees": attendanceStats.totalEmployees,
      "Present Days": attendanceStats.presentDaysTotal,
      "Absent Days": attendanceStats.absentDaysTotal,
    }),
    [
      topbarCompanyName,
      departmentName,
      selectedEmployee,
      startDate,
      endDate,
      attendanceStats,
    ],
  );

  const employeeAttendancePdfColumnCount = useMemo(
    () =>
      Math.max(
        1,
        ...filteredEmployees.map(
          (employee) => employee?.__dailyRecords?.length || 0,
        ),
      ) + 1,
    [filteredEmployees],
  );

  const handleDownloadReport = useCallback(
    async (exportRows, onProgress, downloadFormat = "excel") => {
      const rows = Array.isArray(exportRows) ? exportRows : [];
      if (!rows.length) {
        showToast("info", "No attendance records are available for export.");
        return;
      }

      if (downloadFormat === "pdf" && rows.length > MAX_PDF_ROWS) {
        showToast(
          "info",
          `PDF supports up to ${MAX_PDF_ROWS} rows. This report has ${rows.length} rows. Use Excel instead.`,
        );
        return;
      }

      if (
        downloadFormat === "pdf" &&
        employeeAttendancePdfColumnCount > MAX_PDF_COLUMNS
      ) {
        showToast(
          "info",
          `PDF supports up to ${MAX_PDF_COLUMNS} columns. This report has ${employeeAttendancePdfColumnCount} columns. Use Excel instead.`,
        );
        return;
      }

      const exportSummaryHeaders = [...IDENTITY_EXPORT_HEADERS, ...visibleSummaryHeaders];
      const exportDetailHeaders = [{ name: "Date", key: "Date" }, ...visibleDetailHeaders];

      const groups = rows.map((row) => {
        const summaryFields = {};
        exportSummaryHeaders.forEach((header) => {
          summaryFields[header.key] = row?.[header.key] ?? "-";
        });
        return {
          summaryRow: summaryFields,
          detailRows: row?.__dailyRecords || [],
        };
      });

      const exportFn =
        downloadFormat === "pdf"
          ? downloadLegacyAttendancePdf
          : downloadFormat === "csv"
            ? downloadLegacyAttendanceCsv
            : downloadLegacyAttendanceExcel;

      await exportFn(
        groups,
        exportSummaryHeaders,
        exportDetailHeaders,
        "Employee Attendance Report",
        getReportMetadata(),
      );

      if (typeof onProgress === "function") {
        onProgress(100);
      }
    },
    [
      visibleSummaryHeaders,
      visibleDetailHeaders,
      downloadLegacyAttendanceExcel,
      downloadLegacyAttendancePdf,
      downloadLegacyAttendanceCsv,
      employeeAttendancePdfColumnCount,
      getReportMetadata,
    ],
  );

  const selectedReportTypeOption =
    embeddedReportTypes?.find(
      (option) =>
        option?.label === selectedReportType || option?.value === selectedReportType,
    ) || null;

  const filterControlsRow = useMemo(
    () => (
      <Mui.Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        flexWrap="wrap"
        justifyContent="flex-start"
        sx={{
          width: "100%",
          alignItems: "center",
        }}
      >
        <ApprovalDateRangeFilter
          thisMonthToDate
          startDate={startDate}
          endDate={endDate}
          dateRangeLabel={dateRangeLabel}
          onChange={handleStartDate}
          showToday={true}
          includeCurrentDate={true}
          allowFutureDate={false}
          sx={{
            minHeight: 40,
            height: 40,
            px: 2,
            width: { xs: "100%", sm: "auto" },
            backgroundColor: designColors.slate100,
            border: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: designColors.slate100,
              border: "none",
              boxShadow: "none",
            },
          }}
        />
        <CompactHeaderSelectFilter
          dataList={departmentList}
          selectedValue={departmentName}
          updateDataFunction={handleDepartmentName}
          Icons={PersonOutlineIcon}
          staticLabel="Department"
          widthSx={{
            width: { xs: "100%", sm: "auto" },
            minWidth: { xs: "100%", sm: 200 },
          }}
        />
        <CompactHeaderSelectFilter
          dataList={employeeOptions}
          showEmployeeDetails
          selectedValue={selectedEmployee?.user_name || "All"}
          updateDataFunction={updateEmployee}
          Icons={PersonOutlineIcon}
          staticLabel="Employee"
          widthSx={{
            width: { xs: "100%", sm: "auto" },
            minWidth: { xs: "100%", sm: 200 },
          }}
        />
        <CompactHeaderSelectFilter
          dataList={employeeIdOptions}
          selectedValue={selectedEmployee?.employee_id || "All"}
          updateDataFunction={updateEmployee}
          Icons={BadgeOutlinedIcon}
          staticLabel="Employee ID"
          widthSx={{
            width: { xs: "100%", sm: "auto" },
            minWidth: { xs: "100%", sm: 180 },
          }}
        />
        {embeddedReportTypes?.length ? (
          <CompactHeaderSelectFilter
            dataList={embeddedReportTypes}
            selectedValue={selectedReportTypeOption}
            updateDataFunction={onReportTypeChange}
            Icons={PersonOutlineIcon}
            staticLabel="Report Type"
            widthSx={{
              width: { xs: "100%", sm: "auto" },
              minWidth: { xs: "100%", sm: 260 },
            }}
          />
        ) : null}
      </Mui.Stack>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      startDate,
      endDate,
      dateRangeLabel,
      departmentList,
      departmentName,
      employeeOptions,
      employeeIdOptions,
      selectedEmployee,
      embeddedReportTypes,
      selectedReportTypeOption,
      onReportTypeChange,
    ],
  );

  const toggleEmployeeCard = (key) => {
    setCollapsedEmployees((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const paginatedEmployees = filteredEmployees.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const renderMatrixTable = (employee) => {
    const dailyRecords = employee.__dailyRecords || [];

    return (
      <Mui.TableContainer sx={{ maxHeight: 480, overflow: "auto" }}>
        <Mui.Table stickyHeader size="small">
          <Mui.TableHead>
            <Mui.TableRow>
              <Mui.TableCell
                sx={{
                  position: "sticky",
                  left: 0,
                  top: 0,
                  // Kept below approveStickyFooterBar's zIndex:3 (approveUi.js)
                  // — this corner cell only needs to out-rank the day-header/
                  // row-label cells below it, not the page's sticky footer.
                  // It used to be 4, which tied/beat the footer and let this
                  // cell paint over the pagination bar mid-scroll.
                  zIndex: 2,
                  minWidth: 168,
                  backgroundColor: designColors.slate100,
                  borderBottom: `1px solid ${designColors.slate200}`,
                  borderRight: `1px solid ${designColors.slate200}`,
                  fontWeight: 700,
                  fontSize: 12.5,
                  color: designColors.slate800,
                }}
              >
                Attendance Details
              </Mui.TableCell>
              {dailyRecords.map((day, index) => (
                <Mui.TableCell
                  key={`${employee.__key}-day-head-${index}`}
                  align="center"
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    minWidth: 74,
                    backgroundColor: designColors.slate100,
                    borderBottom: `1px solid ${designColors.slate200}`,
                    p: 0.75,
                  }}
                >
                  <Mui.Box
                    sx={{
                      display: "inline-flex",
                      flexDirection: "column",
                      alignItems: "center",
                      px: 1.1,
                      py: 0.5,
                      borderRadius: 1.5,
                      backgroundColor: `${day?.Color_Code || designColors.slate400}1A`,
                      border: `1px solid ${day?.Color_Code || designColors.slate300}66`,
                    }}
                  >
                    <Mui.Typography sx={{ fontSize: 12.5, fontWeight: 700, color: designColors.slate900 }}>
                      {parseRecordDate(day?.Date).getDate() || "-"}
                    </Mui.Typography>
                    <Mui.Typography sx={{ fontSize: 9.5, color: designColors.slate600, letterSpacing: 0.4 }}>
                      {parseRecordDate(day?.Date)
                        .toLocaleDateString("en-US", { weekday: "short" })
                        .toUpperCase()}
                    </Mui.Typography>
                  </Mui.Box>
                </Mui.TableCell>
              ))}
            </Mui.TableRow>
          </Mui.TableHead>
          <Mui.TableBody>
            {visibleDetailHeaders.length === 0 ? (
              <Mui.TableRow>
                <Mui.TableCell colSpan={dailyRecords.length + 1} align="center">
                  No detail rows selected.
                </Mui.TableCell>
              </Mui.TableRow>
            ) : (
              visibleDetailHeaders.map((header) => (
                <Mui.TableRow key={`${employee.__key}-${header.key}`}>
                  <Mui.TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 0,
                      minWidth: 168,
                      backgroundColor: designColors.slateBg,
                      borderRight: `1px solid ${designColors.slate200}`,
                      borderBottom: `1px solid ${designColors.slate200}`,
                      fontWeight: 600,
                      fontSize: 13,
                      color: designColors.slate800,
                    }}
                  >
                    {header.name}
                  </Mui.TableCell>
                  {dailyRecords.map((day, index) => (
                    <Mui.TableCell
                      key={`${employee.__key}-${header.key}-${index}`}
                      align="center"
                      sx={{
                        minWidth: 74,
                        fontSize: 12.5,
                        color: designColors.slate900,
                        borderBottom: `1px solid ${designColors.slate200}`,
                      }}
                    >
                      {day?.[header.key] ?? "-"}
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              ))
            )}
          </Mui.TableBody>
        </Mui.Table>
      </Mui.TableContainer>
    );
  };

  const renderEmployeeCard = (employee) => {
    const isExpanded = !collapsedEmployees[employee.__key];

    return (
      <Box key={employee.__key} sx={{ ...designShell.sectionCard, mb: 1.5, overflow: "hidden" }}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ p: 2 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar
              sx={{ width: 44, height: 44 }}
              alt={employee?.Name?.charAt(0)?.toUpperCase()}
              src={employee?.image_url ?? optionalAvathar}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 15, color: designColors.slate900 }}>
                {employee?.Name || "-"}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: designColors.slate600 }}>
                {employee?.employee_id || "-"} • {employee?.Dept || "-"}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            size="small"
            onClick={() => toggleEmployeeCard(employee.__key)}
            sx={{
              backgroundColor: designColors.slate100,
              "&:hover": { backgroundColor: designColors.slate200 },
            }}
          >
            <ArrowRightIcon
              sx={{
                color: designColors.slate500,
                cursor: "pointer",
                transition: "transform 0.2s ease",
                transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
              }}
            />
          </IconButton>
        </Stack>

        <Box
          sx={{
            px: 2,
            pb: 2,
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(3, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
              lg: "repeat(6, minmax(0, 1fr))",
            },
            gap: 1.5,
          }}
        >
          {visibleSummaryHeaders.map((header) => (
            <Box key={header.key}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: designColors.slate500,
                  textTransform: "uppercase",
                  letterSpacing: 0.3,
                }}
              >
                {header.name}
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: designColors.slate900 }}>
                {employee?.[header.key] ?? "-"}
              </Typography>
            </Box>
          ))}
        </Box>

        {isExpanded ? (
          <Box sx={{ borderTop: `1px solid ${designColors.slate200}` }}>
            {renderMatrixTable(employee)}
          </Box>
        ) : null}
      </Box>
    );
  };

  return (
    <ThemeProvider theme={withDesignFont(theme)}>
      <FilterProvider domain={domain} userDetails={userDetails} subModuleId={subModuleId}>
        <Box sx={{ pb: 0, width: "100%", maxWidth: "100%" }}>
          <Box sx={{ ...designShell.sectionCard, p: 1.5, mb: 2 }}>{filterControlsRow}</Box>

          {isLoading ? (
            <Loader info="Loading..." />
          ) : (
            <>
              <Mui.Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                    lg: "repeat(4, minmax(0, 1fr))",
                    xl: "repeat(7, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                {summaryStatCards.map((card) => (
                  <StatCard
                    key={card.key}
                    icon={card.icon}
                    title={card.title}
                    value={card.value}
                    detail={card.detail}
                    accent={card.accent}
                    tint={card.tint}
                  />
                ))}
              </Mui.Box>

              <Box sx={{ mb: 1.5 }}>
                <CustomFilter
                  title="Employee Attendance"
                  titleOverride={titleOverride}
                  filterHeaders={SEARCH_FILTER_HEADERS}
                  data={summaryRows}
                  filteredData={filteredEmployees}
                  onFilteredDataChange={handleFilteredDataChange}
                  screenAccess={checkAccess}
                  isAttendanceScreenReport={true}
                  showtitle_count={true}
                  hideCount={true}
                  showDownloadOptions={false}
                  downloadFormats={["excel", "pdf", "csv"]}
                  includeMetadata={true}
                  metadata={getReportMetadata()}
                  customDownloadHandler={handleDownloadReport}
                  pdfRowCountOverride={filteredEmployees.length}
                  pdfColumnCountOverride={employeeAttendancePdfColumnCount}
                  labeledToolbar={true}
                  searchPlaceholder="Search by employee, department..."
                />
              </Box>

              {paginatedEmployees.length === 0 ? (
                <Box sx={{ ...designShell.sectionCard, p: 3 }}>
                  <Mui.Alert severity="info">
                    {summaryRows.length === 0
                      ? "No data available."
                      : "No matching data found for your search."}
                  </Mui.Alert>
                </Box>
              ) : (
                paginatedEmployees.map(renderEmployeeCard)
              )}
            </>
          )}

          {footerHeight ? <Box sx={{ height: footerHeight }} /> : null}
          <Mui.Box
            ref={footerRef}
            sx={{ ...approveStickyFooterBar, mt: 1.5, borderRadius: 0 }}
          >
            {paginationFooter({
              page,
              rowsPerPage,
              total: filteredEmployees.length,
              itemLabel: "employees",
              onPageChange: setPage,
              onRowsPerPageChange: handleChangeRowsPerPage,
              rowsPerPageOptions,
            })}
          </Mui.Box>
        </Box>
      </FilterProvider>
    </ThemeProvider>
  );
};


